
import { useState, useCallback, useEffect, useRef } from 'react';
import { getErrorMessage } from '@/utils/errors';
import { useGeolocationService } from '@/hooks/map/useGeolocationService';
import { useGeocoding, type LocationFeature } from '@/hooks/map/useGeocoding';
import { useLocationBias } from '@/hooks/map/useLocationBias';
import { useRecentLocations } from '@/hooks/useRecentLocations';
import { useToast } from '@/components/ui/use-toast';
import { useDebounce } from '@/hooks/useDebounce';

interface UseLocationInputsProps {
  initialPickup: string;
  initialDestination: string;
  onPickupChange: (location: string) => void;
  onDestinationChange: (destination: string) => void;
}

/**
 * A suggestion list together with the query it answers.
 *
 * Keeping the two apart is what let the field read "Reading Terminal Market"
 * while the list below it showed rows for "Independence Hall" — the previous
 * query's response landed second and overwrote the current one. Storing them
 * together means a response can only ever be rendered under its own query.
 */
interface Answered {
  query: string;
  features: LocationFeature[];
}

const EMPTY: Answered = { query: '', features: [] };

/** A location the user actually picked, and where it is. */
interface Picked {
  value: string;
  center: [number, number];
}

export const useLocationInputs = ({
  initialPickup,
  initialDestination,
  onPickupChange,
  onDestinationChange
}: UseLocationInputsProps) => {
  // Local state for input values - independent of the debounced versions
  const [localPickup, setLocalPickup] = useState(initialPickup);
  const [localDestination, setLocalDestination] = useState(initialDestination);

  // The text each field held when the user last finished with it — by picking
  // a suggestion, by using their current location, or by leaving the field.
  // Half-typed text is not an address, and routing it is what produced
  // "Failed to load route" while someone was still typing.
  const [committedPickup, setCommittedPickup] = useState(initialPickup);
  const [committedDestination, setCommittedDestination] = useState(initialDestination);

  // Location suggestion states, each tagged with the query it answers.
  const [pickupAnswer, setPickupAnswer] = useState<Answered>(EMPTY);
  const [destinationAnswer, setDestinationAnswer] = useState<Answered>(EMPTY);
  const [pickupFetching, setPickupFetching] = useState(false);
  const [destinationFetching, setDestinationFetching] = useState(false);

  // Where the chosen endpoints are, when the user chose them from the list.
  // The suggestion carried its coordinates, so keeping them here saves the
  // round-trip that would otherwise re-geocode a string we already resolved —
  // and removes the chance of the geocoder answering with a *different* place
  // than the row that was clicked.
  const [pickupPick, setPickupPick] = useState<Picked | null>(null);
  const [destinationPick, setDestinationPick] = useState<Picked | null>(null);

  // Custom hooks
  const { geocodeLocation, reverseGeocode, getAddressSuggestions } = useGeocoding();
  const { getUserLocation, isGettingLocation, error: locationError } = useGeolocationService();
  const { recentLocations, addRecentLocation } = useRecentLocations(8);
  const { toast } = useToast();

  // Where to bias address ranking. Without one, Mapbox ranks by global
  // prominence and a rider in Philadelphia searching "30th street station" is
  // offered Sacramento and Fort Worth first. Read only if the rider has
  // already granted location permission — see useLocationBias.
  const userBias = useLocationBias();

  // A pick is only current while the field still holds the text that was
  // picked; typing over it invalidates the coordinates without extra
  // bookkeeping.
  const pickupCenter = pickupPick?.value === localPickup ? pickupPick.center : null;
  const destinationCenter =
    destinationPick?.value === localDestination ? destinationPick.center : null;

  // The chosen pickup is the better bias for the destination field, and it
  // costs nothing: the suggestion the rider clicked carried its centre. This
  // used to read `pickupSuggestions[0].center` — the top *candidate* for
  // whatever was being typed, which changed on every keystroke and so
  // re-issued every destination request with a different bias.
  const destinationBias = pickupCenter ?? userBias ?? undefined;
  const pickupBias = userBias ?? undefined;

  // The single debounce in this chain, owned by the code that makes the
  // request. 200ms keeps typeahead inside the 400ms Doherty bar even with a
  // round-trip; it was 500ms here on top of another 300ms in useLocationInput,
  // and they are sequential, so 800ms passed before the fetch even started.
  const debouncedPickup = useDebounce(localPickup, 200);
  const debouncedDestination = useDebounce(localDestination, 200);

  // Update parent state when debounced values change
  useEffect(() => {
    if (debouncedPickup !== initialPickup) {
      onPickupChange(debouncedPickup);
    }
  }, [debouncedPickup, onPickupChange, initialPickup]);

  useEffect(() => {
    if (debouncedDestination !== initialDestination) {
      onDestinationChange(debouncedDestination);
    }
  }, [debouncedDestination, onDestinationChange, initialDestination]);

  // Update local state when props change (e.g. from parent). Uses the
  // functional updater so these don't need `localPickup`/`localDestination`
  // in the dependency array — depending on them directly would re-run on
  // every keystroke and fight the user's typing.
  useEffect(() => {
    setLocalPickup((prev) => (prev === initialPickup ? prev : initialPickup));
  }, [initialPickup]);

  useEffect(() => {
    setLocalDestination((prev) => (prev === initialDestination ? prev : initialDestination));
  }, [initialDestination]);

  // Prefill the pickup with where the rider actually is.
  //
  // This is the ride-hailing convention — nobody types their own address to
  // get picked up from where they are standing — and it is the difference
  // between two fields to fill and one. It costs a reverse-geocode, which is
  // why it is gated three ways: only when `useLocationBias` produced a fix
  // (which it only does when location permission was ALREADY granted, so this
  // never triggers a prompt), only while the field is still empty, and only
  // once per mount. A rider who has started typing is never overwritten.
  const prefilledRef = useRef(false);
  useEffect(() => {
    if (prefilledRef.current || userBias == null || localPickup !== '') return;
    prefilledRef.current = true;

    let cancelled = false;
    void (async () => {
      const address = await reverseGeocode(userBias);
      // reverseGeocode answers with these rather than throwing.
      if (cancelled || address === 'Unknown location' || address === 'Location unavailable') {
        return;
      }
      // Re-check: the rider may have typed while the request was in flight.
      setLocalPickup((prev) => (prev === '' ? address : prev));
      setCommittedPickup((prev) => (prev === '' ? address : prev));
      // The fix we reverse-geocoded *is* this address's location, so the
      // prefilled pickup starts out with coordinates like a picked one.
      setPickupPick({ value: address, center: userBias });
    })();

    return () => {
      cancelled = true;
    };
  }, [userBias, localPickup, reverseGeocode]);

  // Handle user selecting current location
  const handleCurrentLocation = useCallback(async () => {
    try {
      toast({
        title: "Getting your location",
        description: "Please wait while we locate your position...",
      });

      const coords = await getUserLocation();
      const address = await reverseGeocode(coords);
      setLocalPickup(address);
      setCommittedPickup(address);
      setPickupPick({ value: address, center: coords });
      addRecentLocation(address);

      toast({
        title: "Location found",
        description: "Your current location has been set as the pickup point.",
      });
    } catch (error) {
      console.error('Error getting current location:', error);
      toast({
        title: "Location error",
        description: getErrorMessage(error) || "Could not access your location. Please check your browser permissions.",
        variant: "destructive",
      });
    }
  }, [getUserLocation, reverseGeocode, toast, addRecentLocation]);

  // Fetch address suggestions for pickup location
  const pickupSeq = useRef(0);
  useEffect(() => {
    const seq = ++pickupSeq.current;

    // Don't fetch suggestions for empty or very short queries
    if (!debouncedPickup || debouncedPickup.length < 2) {
      setPickupAnswer(EMPTY);
      setPickupFetching(false);
      return;
    }

    setPickupFetching(true);
    void (async () => {
      try {
        const features = await getAddressSuggestions(debouncedPickup, {
          proximity: pickupBias,
        });
        // A response that is no longer the latest is not an answer to
        // anything on screen. Dropping it is what keeps the rows under the
        // field and the text in the field describing the same place.
        if (seq !== pickupSeq.current) return;
        setPickupAnswer({ query: debouncedPickup, features });
      } catch (error) {
        console.error("Error fetching pickup suggestions:", error);
        if (seq !== pickupSeq.current) return;
        setPickupAnswer({ query: debouncedPickup, features: [] });
      } finally {
        if (seq === pickupSeq.current) setPickupFetching(false);
      }
    })();
  }, [debouncedPickup, getAddressSuggestions, pickupBias]);

  // Fetch address suggestions for destination
  const destinationSeq = useRef(0);
  useEffect(() => {
    const seq = ++destinationSeq.current;

    if (!debouncedDestination || debouncedDestination.length < 2) {
      setDestinationAnswer(EMPTY);
      setDestinationFetching(false);
      return;
    }

    setDestinationFetching(true);
    void (async () => {
      try {
        const features = await getAddressSuggestions(debouncedDestination, {
          proximity: destinationBias,
        });
        if (seq !== destinationSeq.current) return;
        setDestinationAnswer({ query: debouncedDestination, features });
      } catch (error) {
        console.error("Error fetching destination suggestions:", error);
        if (seq !== destinationSeq.current) return;
        setDestinationAnswer({ query: debouncedDestination, features: [] });
      } finally {
        if (seq === destinationSeq.current) setDestinationFetching(false);
      }
    })();
  }, [debouncedDestination, getAddressSuggestions, destinationBias]);

  // Only ever hand a field the rows that answer what it currently holds.
  const pickupSuggestions =
    pickupAnswer.query === localPickup ? pickupAnswer.features : [];
  const destinationSuggestions =
    destinationAnswer.query === localDestination ? destinationAnswer.features : [];

  // "Busy" for a field means: a request is out, OR the debounce has not caught
  // up with what is typed, OR what came back answers an older query. All three
  // are states in which there is no answer yet — and saying "no address
  // suggestions found" in any of them is a false negative. There used to be a
  // single `isLoading` shared by both fields for exactly this, so typing in
  // one field could declare the other one empty.
  const pickupBusy =
    pickupFetching || debouncedPickup !== localPickup || pickupAnswer.query !== localPickup;
  const destinationBusy =
    destinationFetching ||
    debouncedDestination !== localDestination ||
    destinationAnswer.query !== localDestination;

  /** The coordinates the list already carries for a row the user clicked. */
  const centerFor = (features: LocationFeature[], value: string): [number, number] | undefined =>
    features.find((f) => f.place_name === value)?.center;

  // Handle selection of a suggested location
  const handleSelectPickupLocation = useCallback((location: string) => {
    setLocalPickup(location);
    const center = centerFor(pickupAnswer.features, location);
    setPickupPick(center ? { value: location, center } : null);
    addRecentLocation(location);
  }, [addRecentLocation, pickupAnswer.features]);

  // A field is "committed" once the user is done with it. Anything shorter
  // than a plausible address is treated as still being typed.
  const isPlausibleAddress = (value: string) => value.trim().length >= 5;

  const commitPickup = useCallback((value: string) => {
    setCommittedPickup(isPlausibleAddress(value) ? value : '');
  }, []);

  const commitDestination = useCallback((value: string) => {
    setCommittedDestination(isPlausibleAddress(value) ? value : '');
  }, []);

  const handleSelectDestination = useCallback((location: string) => {
    setLocalDestination(location);
    const center = centerFor(destinationAnswer.features, location);
    setDestinationPick(center ? { value: location, center } : null);
    addRecentLocation(location);
  }, [addRecentLocation, destinationAnswer.features]);

  // Both endpoints settled and unchanged since. This is the gate on drawing a
  // route: the map should show where someone is going, not react to keystrokes.
  // Coordinates ride along when we have them, so the map can skip geocoding.
  const routeEndpoints =
    committedPickup &&
    committedDestination &&
    committedPickup === localPickup &&
    committedDestination === localDestination
      ? {
          from: committedPickup,
          to: committedDestination,
          fromCoords: pickupCenter ?? undefined,
          toCoords: destinationCenter ?? undefined,
        }
      : null;

  return {
    // States
    localPickup,
    localDestination,
    pickupSuggestions,
    destinationSuggestions,
    recentLocations,

    // Actions
    setLocalPickup,
    setLocalDestination,
    handleCurrentLocation,
    handleSelectPickupLocation,
    handleSelectDestination,
    commitPickup,
    commitDestination,

    // Derived
    routeEndpoints,

    // Status — per field, because a shared flag makes one field lie about the
    // other. `isGeocodingLoading` is kept as the OR for callers that render a
    // single spinner for the pair.
    isPickupLoading: pickupBusy,
    isDestinationLoading: destinationBusy,
    isGeocodingLoading: pickupBusy || destinationBusy,
    isGettingLocation,
  };
};
